import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import type { FocusEvent, KeyboardEvent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import U2000Label from "../label/U2000Label";
import U2000KeyboardKeyHint from "../../common/U2000KeyboardKeyHint";
import { FaSearch, FaExclamationTriangle } from "react-icons/fa";
import type { U2000AutoCompleteSearchResult } from "../../../types/U2000AutoCompleteSearchResult";
import { U2000AutoCompleteSearchCountFooter } from "../../common/U2000AutoCompleteSearchCountFooter";
import { u2000NormalizeAutoCompleteSearchResult } from "../../../utils/u2000NormalizeAutoCompleteSearchResult";
import { u2000IsEditableMouseDownTarget } from "../../../utils/u2000IsEditableMouseDownTarget";
import { u2000IsCssResizeHandleMouseDown } from "../../../utils/u2000IsCssResizeHandleMouseDown";
import { u2000ShouldOpenAutoCompleteResultsAfterSearch } from "../../../utils/u2000ShouldOpenAutoCompleteResultsAfterSearch";
import { u2000ShouldCloseAutoCompleteOnRelatedFocus } from "../../../utils/u2000ShouldCloseAutoCompleteOnRelatedFocus";
import { useU2000AuthContext } from "../../../context/auth/U2000UseAuthContext";
import { isMenuPathAllowed } from "../../../context/auth/U2000AuthUtils";
import { u2000BuildAppRouteHref } from "../../../tools/u2000OpenAppRouteInNewTab";
import {
  u2000BuildAutocompleteEntityFormPath,
  u2000CanOpenAutocompleteEntityLink,
} from "../../../tools/u2000AutocompleteEntityLink";

// Types utilitaires
type GenericObject = Record<string, any>;

interface U2000AutoCompleteInputResultTableProps {
  results: GenericObject[];
  resultLabels?: string[];
  resultFields?: string[];
  handleRowClick: (item: GenericObject, idx: number) => void;
  showResults: boolean;
  setShowResults: (show: boolean) => void;
  resultTableHeight?: number;
  highlightedIndex: number;
  setHighlightedIndex: Dispatch<SetStateAction<number>>;
  className?: string;
  listId?: string;
  /** Si false, n'affiche pas la ligne d'en-tête du tableau (défaut true). */
  showHeader?: boolean;
  /** Contenu d'une cellule (sinon `item[field]`). */
  formatResultCell?: (item: GenericObject, field: string) => string;
  /** En-tête affiché seulement si cette fonction retourne true (sinon même règle que showHeader). */
  resolveShowResultHeader?: (results: GenericObject[]) => boolean;
  totalCount?: number;
  truncated?: boolean;
}

interface U2000InvalidFeedbackAutoCompleteProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
}

interface U2000AutoCompleteInputProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  mainFieldName?: string;
  displayField?: string;
  displayFn?: (item: GenericObject) => string;
  overrideExtractDataFn?: (item: GenericObject | null) => { fieldName: string; value: any }[];
  returnFields?: string[];
  placeholder?: string;
  onSearch?: (criteriasArray: { fieldName: string; value: any }[]) => Promise<GenericObject[] | U2000AutoCompleteSearchResult<GenericObject>>;
  renderPanel?: (props: any) => React.ReactNode;
  panelWidth?: number;
  /** Largeur du dropdown simple (liste sous le champ). */
  dropdownWidth?: number;
  customSearchPanelHeight?: number;
  minCarLaunchSearch?: number;
  pageSize?: number;
  label?: string;
  readonly?: boolean;
  required?: boolean;
  onSetCurrentSelection?: (setCurrentSelection: (item: GenericObject | null) => void, setInputValue: (val: string) => void) => void;
  getValidations?: any;
  inputRef?: React.RefObject<HTMLInputElement>;
  requestFocus?: boolean;
  resultLabels?: string[];
  resultFields?: string[];
  basicResultHeight?: number;
  /** Si false, masque l'en-tête du tableau de résultats dans le dropdown simple (défaut true). */
  resultTableShowHeader?: boolean;
  formatResultCell?: (item: GenericObject, field: string) => string;
  resolveShowResultHeader?: (results: GenericObject[]) => boolean;
  /**
   * BL-0421 — Si true (défaut), la valeur sélectionnée peut être un hyperlien vers la fiche UL
   * lorsque `entityLink` est mappé et que l’usager a le droit. Passer false en contexte search
   * si un wrapper réutilise ce composant (les critères liste utilisent `U2000CriteriaAutoComplete`).
   */
  enableEntityLink?: boolean;
  /**
   * BL-0421 — Code UL cible (`U3120`, `U3121`, `U3101`, `U3125`, …).
   * Absent ou hors registre → pas de lien (pays, province, etc.).
   */
  entityLink?: string;
  /** Grille compacte U3129 (h-8) — coque champ + overlay sélection. */
  compactGrid?: boolean;
  [key: string]: any;
}

// Liste des resultat si plus d'une valeurs
export const U2000AutoCompleteInputResultTable: React.FC<U2000AutoCompleteInputResultTableProps> = ({
  results,
  resultLabels,
  resultFields,
  handleRowClick,
  showResults,
  setShowResults,
  resultTableHeight,
  highlightedIndex,
  setHighlightedIndex,
  className,
  listId,
  showHeader = true,
  formatResultCell,
  resolveShowResultHeader,
  totalCount,
  truncated: _truncated,
}) => {
  const showCadastralColumns = resolveShowResultHeader ? resolveShowResultHeader(results) : true;
  const visibleResultFields =
    Array.isArray(resultFields) && resultFields.length > 0
      ? showCadastralColumns
        ? resultFields
        : [resultFields[0]]
      : resultFields;
  const visibleResultLabels =
    Array.isArray(resultLabels) && resultLabels.length > 0
      ? showCadastralColumns
        ? resultLabels
        : [resultLabels[0]]
      : resultLabels;
  const showHeaderRow =
    showHeader === true &&
    showCadastralColumns &&
    visibleResultLabels &&
    Array.isArray(visibleResultLabels) &&
    visibleResultLabels.length > 0;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);

  // Ref pour garder la version à jour de results (évite la stale closure dans le handler global)
  const resultsRef = useRef<GenericObject[]>(results);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  // Gestion des touches pour navigation (utilise resultsRef et setState fonctionnel)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showResults || resultsRef.current.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < 0) {
            return resultsRef.current.length > 0 ? 0 : prev;
          }
          return prev < resultsRef.current.length - 1 ? prev + 1 : prev;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev <= 0) {
            return -1;
          }
          return prev - 1;
        });
      } else if (e.key === "Enter") {
        // utiliser la valeur actuelle via fonction setState pour garantir cohérence
        setHighlightedIndex((prev) => {
          if (prev >= 0 && prev < resultsRef.current.length) {
            handleRowClick(resultsRef.current[prev], prev as number);
            setShowResults(false);
          }
          return prev;
        });
      } else if (e.key === "Escape") {
        // Fermer la liste de résultats si Escape est pressé
        setShowResults(false);
        setHighlightedIndex(-1);
      }
    };
    window.addEventListener("keydown", handleKeyDown as any);
    return () => window.removeEventListener("keydown", handleKeyDown as any);
  }, [showResults, handleRowClick, setShowResults, setHighlightedIndex]);

  useEffect(() => {
    if (highlightedIndex >= 0 && rowRefs.current[highlightedIndex] && tableContainerRef.current) {
      const row = rowRefs.current[highlightedIndex];
      const container = tableContainerRef.current;
      if (row && container) {
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;
        const containerScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        if (rowTop < containerScrollTop) {
          container.scrollTop = rowTop;
        } else if (rowBottom > containerScrollTop + containerHeight) {
          container.scrollTop = rowBottom - containerHeight;
        }
      }
    }
  }, [highlightedIndex, results]);

  return (
    <div
      className={`mt-1 flex min-h-0 flex-col rounded-lg border surface-border bg-surface shadow-xl animate-fade-in ${className ?? ""}`}
      style={resultTableHeight ? { maxHeight: resultTableHeight } : undefined}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div
        role="listbox"
        id={listId}
        className="min-h-0 flex-1 overflow-y-auto"
        ref={tableContainerRef}
        aria-labelledby={listId ? `${listId}-label` : undefined}
      >
        <table className="min-w-full text-sm">
          {showHeaderRow && (
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                {visibleResultLabels!.map((libelle, idx) => (
                  <th key={idx} className="border-b surface-border px-3 py-2.5 text-left text-sm font-medium text-gray-700">
                    {libelle}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {results.map((item, idx) => (
              <tr
                key={idx}
                ref={(el) => {
                  rowRefs.current[idx] = el;
                }}
                id={listId ? `${listId}-option-${idx}` : undefined}
                role="option"
                aria-selected={idx === highlightedIndex}
                className={`cursor-pointer transition-colors duration-200 ${idx === highlightedIndex ? "bg-blue-50 text-blue-900" : "hover:bg-muted text-gray-900"} ${
                  idx < results.length - 1 ? "border-b border-gray-100" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleRowClick(item, idx);
                  setShowResults(false);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {Array.isArray(visibleResultFields) && visibleResultFields.length > 0 ? (
                  visibleResultFields.map((field, colIdx) => (
                    <td key={colIdx} className="px-3 py-2.5 text-sm">
                      {formatResultCell ? formatResultCell(item, field) : (item[field] ?? "")}
                    </td>
                  ))
                ) : (
                  <td className="px-3 py-2.5 text-sm text-gray-500 italic">field not defined</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <U2000AutoCompleteSearchCountFooter displayedCount={results.length} totalCount={totalCount} />
    </div>
  );
};

const U2000InvalidFeedbackAutoComplete: React.FC<U2000InvalidFeedbackAutoCompleteProps> = ({ formStateHandlers, fieldName }) => {
  const errorField = fieldName + ".id";
  const touched = formStateHandlers.getTouched();
  const errors = formStateHandlers.getErrors();
  const isSubmitted = formStateHandlers.isSubmitted();
  const hasError = (touched[fieldName] || isSubmitted) && errors[errorField];

  if (!hasError) return null;

  return (
    <div className="flex items-start gap-1.5 mt-1.5 animate-fade-in">
      <FaExclamationTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-red-600 leading-tight" role="alert" aria-live="polite">
        {errors[errorField]}
      </p>
    </div>
  );
};

const U2000AutoCompleteInput: React.FC<U2000AutoCompleteInputProps> = ({
  formStateHandlers,
  fieldName,
  mainFieldName = "search",
  displayField = "label",
  displayFn,
  overrideExtractDataFn,
  returnFields = [],
  placeholder = "",
  onSearch,
  renderPanel,
  panelWidth = undefined,
  dropdownWidth = undefined,
  customSearchPanelHeight: panelHeight = undefined,
  minCarLaunchSearch = 3,
  pageSize = 25,
  label,
  readonly = false,
  required = false,
  onSetCurrentSelection,
  getValidations,
  enableEntityLink = true,
  entityLink,
  compactGrid = false,
  ...rest
}) => {
  const { t } = useTranslation("common");
  const { t: tU2000 } = useTranslation("u2000");
  const { auth, hasRole } = useU2000AuthContext();
  const [value, setValue] = useState<string>("");
  const [results, setResults] = useState<GenericObject[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPanel, setShowPanel] = useState<boolean>(false);
  const [panelFields, setPanelFields] = useState<GenericObject>({});
  /** Taille après drag CSS resize — survit aux re-renders (BL-0350). */
  const [userPanelSize, setUserPanelSize] = useState<{ width?: number; height?: number }>({});
  // NodeJS.Timeout n'est pas reconnu côté navigateur, utiliser number
  const debounceRef = useRef<number | null>(null);
  const lastSearchedValue = useRef<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const f8PanelRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const resultListIdRef = useRef<string>(`u2000-ac-${Math.random().toString(36).slice(2, 9)}`);
  const hasUserFocusedRef = useRef<boolean>(false);
  const [currentSelection, setCurrentSelection] = useState<GenericObject | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [searchMeta, setSearchMeta] = useState<{ totalCount?: number; truncated?: boolean }>({});
  const [onInput, setOnInput] = useState(false);
  const inputElRef = useRef<HTMLInputElement | null>(null);
  const invalidFeedbackRef = useRef<HTMLDivElement | null>(null);
  const blurTimeoutRef = useRef<number | null>(null);
  const displayFnRef = useRef(displayFn);
  const displayFieldRef = useRef(displayField);
  const prevShowPanelRef = useRef(false);
  displayFnRef.current = displayFn;
  displayFieldRef.current = displayField;
  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  const entityId =
    currentSelection != null && currentSelection.id != null ? currentSelection.id : null;
  const canOpenEntityLink = useMemo(
    () =>
      u2000CanOpenAutocompleteEntityLink({
        enableEntityLink,
        entityLink,
        entityId,
        hasRole,
        isLogicalUnitInMenu: (listPath) => isMenuPathAllowed(auth?.menu ?? [], listPath),
      }),
    [enableEntityLink, entityLink, entityId, hasRole, auth?.menu],
  );
  const entityFormPath =
    canOpenEntityLink && entityLink && entityId != null
      ? u2000BuildAutocompleteEntityFormPath(entityLink, entityId)
      : null;
  const openEntityInNewTabLabel = tU2000("lblOpenEntityInNewTab");
  const selectedDisplayText =
    currentSelection == null
      ? ""
      : displayFn
        ? displayFn(currentSelection)
        : (currentSelection[displayField] ?? "");

  /** Réinitialise critères et résultats du panneau latéral (F8 / loupe). */
  const resetPanelSearchState = useCallback(() => {
    setPanelFields({});
    setResults([]);
    setSearchMeta({});
    setShowResults(false);
  }, []);

  useEffect(() => {
    if (prevShowPanelRef.current && !showPanel) {
      resetPanelSearchState();
      setUserPanelSize({});
    }
    prevShowPanelRef.current = showPanel;
  }, [showPanel, resetPanelSearchState]);

  // Persiste la taille du panneau F8 après resize CSS natif (BL-0350).
  useEffect(() => {
    if (!showPanel) {
      return;
    }
    const el = f8PanelRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      // offset* = border-box (évite boucle de shrink avec contentRect + padding).
      const nextWidth = Math.round(el.offsetWidth);
      const nextHeight = Math.round(el.offsetHeight);
      if (nextWidth <= 0 || nextHeight <= 0) {
        return;
      }
      setUserPanelSize((prev) => {
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev;
        }
        return { width: nextWidth, height: nextHeight };
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showPanel]);
  // Forward internal input ref to optional external ref passed via props
  useEffect(() => {
    if (rest.inputRef && typeof rest.inputRef === "object") {
      try {
        (rest.inputRef as React.MutableRefObject<HTMLInputElement | null>).current = inputElRef.current;
      } catch (err) {
        // ignore if not assignable
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputElRef.current]);

  // If parent requested focus, focus/select the input when available
  const focusRequested = (rest.requestFocus ?? (rest as any).autoFocus) as boolean | undefined;

  useEffect(() => {
    if (focusRequested) {
      setTimeout(() => {
        try {
          if (inputElRef.current) {
            inputElRef.current.focus();
            if (typeof inputElRef.current.select === "function") inputElRef.current.select();
          }
        } catch (err) {
          // ignore
        }
      }, 0);
    }
  }, [focusRequested]);

  useEffect(() => {
    if (!currentSelection) {
      if (onInput) {
        const data = formStateHandlers.getData() as Record<string, unknown> | undefined;
        const fieldVal = fieldName.split(".").reduce((acc: unknown, part: string) => (acc != null && typeof acc === "object" ? (acc as Record<string, unknown>)[part] : undefined), data);
        const isFieldAlreadyEmpty =
          fieldVal == null ||
          (typeof fieldVal === "object" &&
            ((fieldVal as Record<string, unknown>).id === undefined || (fieldVal as Record<string, unknown>).id === null || (fieldVal as Record<string, unknown>).id === ""));
        if (isFieldAlreadyEmpty) return;

        let extractedData: { fieldName: string; value: any }[] = [];
        if (overrideExtractDataFn) {
          extractedData = overrideExtractDataFn(null);
        }
        const currentValue = JSON.parse(JSON.stringify(formStateHandlers.getData() || {}));
        if (extractedData) {
          extractedData.forEach(({ fieldName: fn, value }) => {
            setNestedValue(currentValue, fn, value);
          });
        }
        formStateHandlers.setData(currentValue);
      }
      return;
    }
    const fn = displayFnRef.current;
    const dispField = displayFieldRef.current;
    const displayValue = fn ? fn(currentSelection) : currentSelection[dispField];
    setResults([]);
    setSearchMeta({});
    setValue(displayValue);
    lastSearchedValue.current = displayValue;
    setInputValue(displayValue);
    setHighlightedIndex(-1);
  }, [currentSelection, fieldName]);

  // compute current active option id
  const activeOptionId = (highlightedIndex >= 0 ? `${resultListIdRef.current}-option-${highlightedIndex}` : undefined) as string | undefined;

  useEffect(() => {
    if (typeof onSetCurrentSelection === "function") {
      onSetCurrentSelection(setCurrentSelection, setInputValue);
    }
  }, [onSetCurrentSelection]);

  useEffect(() => {
    if (showPanel) return;
    if (inputValue.length < minCarLaunchSearch) {
      setResults([]);
      setSearchMeta({});
      setShowResults(false);
      lastSearchedValue.current = "";
      setLoading(false);
      setHighlightedIndex(-1);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      const allowEmptySearch = minCarLaunchSearch === 0 && currentSelection === null && (inputValue !== "" || hasUserFocusedRef.current);
      if (onSearch && (inputValue !== lastSearchedValue.current || allowEmptySearch)) {
        lastSearchedValue.current = inputValue;
        setLoading(true);
        const criterias = { [mainFieldName]: inputValue, pageSize: String(pageSize) };
        const criteriasArray = criteriasToFieldArray(criterias);
        const raw = await onSearch(criteriasArray);
        const normalized = u2000NormalizeAutoCompleteSearchResult(raw);
        setSearchMeta({ totalCount: normalized.totalCount, truncated: normalized.truncated });
        if (normalized.results.length === 1) {
          let extractedData: { fieldName: string; value: any }[] = [];
          if (overrideExtractDataFn) {
            extractedData = overrideExtractDataFn(normalized.results[0]);
          }
          const currentValue = JSON.parse(JSON.stringify(formStateHandlers.getData() || {}));
          if (extractedData) {
            extractedData.forEach(({ fieldName, value }) => {
              setNestedValue(currentValue, fieldName, value);
            });
          }
          formStateHandlers.setData(currentValue);
          setCurrentSelection(normalized.results[0]);
        } else {
          setResults(normalized.results);
          const shouldOpen = u2000ShouldOpenAutoCompleteResultsAfterSearch({
            inputIsActive: document.activeElement === inputElRef.current,
            blurClosePending: blurTimeoutRef.current != null,
          });
          if (shouldOpen) {
            setShowResults(true);
            setHighlightedIndex(-1);
          }
        }
        setLoading(false);
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, onInput]);

  // Fonction utilitaire pour obtenir la position de l'input (coords viewport pour position:fixed)
  const [inputPos, setInputPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  useLayoutEffect(() => {
    if (inputElRef.current) {
      const rect = inputElRef.current.getBoundingClientRect();
      setInputPos({ x: rect.left, y: rect.top });
    }
  }, [showPanel, showResults]); // Recalculer la position quand panel ou résultats s'affichent

  // Gestion des touches pour navigation (utilise resultsRef et setState fonctionnel)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "F8") {
        // N'ouvrir le panel que si un panel de recherche est fourni
        if (renderPanel && !showPanel) {
          resetPanelSearchState();
          setShowPanel(true);
        }
      } else if (e.key === "Escape") {
        // Si le panel est ouvert, le fermer. Sinon fermer le dropdown de résultats.
        if (showPanel) {
          setShowPanel(false);
        } else if (showResults) {
          setShowResults(false);
          setOnInput(true);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown as any);
    return () => window.removeEventListener("keydown", handleKeyDown as any);
  }, [showPanel, showResults, setShowResults, setShowPanel, setOnInput, renderPanel, resetPanelSearchState]);

  // Initialiser currentSelection et inputValue à partir des données du formulaire
  // Dépendance stable : on utilise une clé dérivée des données du champ pour éviter la boucle infinie
  const formData = formStateHandlers.getData();
  const fieldValue =
    formData != null && typeof formData === "object"
      ? fieldName.split(".").reduce((acc: unknown, part: string) => (acc != null && typeof acc === "object" ? (acc as Record<string, unknown>)[part] : undefined), formData as Record<string, unknown>)
      : undefined;
  const fieldId = fieldValue != null && typeof fieldValue === "object" ? (fieldValue as Record<string, unknown>).id : undefined;
  const formFieldKey = fieldId != null ? String(fieldId) : "";

  useEffect(() => {
    const fieldNameId = fieldName + ".id";
    const id = getNestedValue(formStateHandlers, fieldNameId);
    const fv = getNestedValue(formStateHandlers, fieldName);

    const isValidId = id && ((typeof id === "string" && id.trim() !== "") || (typeof id === "number" && id > 0));
    const fieldValueId = fv && typeof fv === "object" ? fv.id : null;
    const isValidFieldValueId = fieldValueId && ((typeof fieldValueId === "string" && fieldValueId.trim() !== "") || (typeof fieldValueId === "number" && fieldValueId > 0));
    const hasValidData = fv && typeof fv === "object" && isValidFieldValueId;

    if (isValidId || hasValidData) {
      const valueToUse = fv && typeof fv === "object" ? fv : null;
      if (valueToUse) {
        const dispField = displayFieldRef.current;
        const fn = displayFnRef.current;
        const newId = valueToUse.id ?? valueToUse[dispField];
        const sameAsCurrent = currentSelection && (currentSelection.id === newId || currentSelection[dispField] === newId);
        if (!sameAsCurrent) {
          setCurrentSelection(valueToUse);
          const displayValue = fn ? fn(valueToUse) : valueToUse[dispField] || "";
          setInputValue(displayValue);
          setValue(displayValue);
        }
      }
    } else {
      // Ne pas effacer inputValue/value quand l'utilisateur est en train de taper (onInput)
      const shouldClearInput = !onInput && (inputValue !== "" || value !== "");
      if (currentSelection != null || shouldClearInput) {
        setCurrentSelection(null);
        if (!onInput) {
          setInputValue("");
          setValue("");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFieldKey, fieldName, onInput]);

  // Ajout d'un spinner simple inline
  const Spinner: React.FC = () => (
    <svg className="h-5 w-5 animate-spin text-gray-400" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );

  // Fonction pour ouvrir le panel et reset la saisie, les résultats et les champs du panel
  const handleOpenPanel = () => {
    if (isDisabled) return;
    resetPanelSearchState();
    setShowPanel(true);
    setCurrentSelection(null);
    setInputValue("");
  };

  // Fonction pour lancer la recherche avancée depuis le panel
  const handlePanelSearch = async (fields: GenericObject) => {
    setLoading(true);
    setPanelFields(fields);
    if (onSearch) {
      const criterias = { ...fields, pageSize: String(pageSize) };
      const criteriasArray = criteriasToFieldArray(criterias);
      const raw = await onSearch(criteriasArray);
      const normalized = u2000NormalizeAutoCompleteSearchResult(raw);
      setSearchMeta({ totalCount: normalized.totalCount, truncated: normalized.truncated });
      setResults(normalized.results);
      setShowResults(true);
    }
    setLoading(false);
  };

  function getNestedValue<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, fieldName: string): any {
    const data = formStateHandlers.getData() as Record<string, any>;
    return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
  }

  function setNestedValue(obj: GenericObject, path: string, value: any): GenericObject {
    const keys = path.split(".");
    const lastKey = keys.pop()!;
    const deep = keys.reduce((acc, key) => {
      if (!acc[key]) acc[key] = {};
      return acc[key];
    }, obj);
    deep[lastKey] = value;
    return obj;
  }

  // Ajoute la fonction handleRowClick si elle n'existe pas déjà
  function handleRowClick(item: GenericObject) {
    if (blurTimeoutRef.current != null) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    let extractedData: { fieldName: string; value: any }[] = [];
    if (overrideExtractDataFn) {
      extractedData = overrideExtractDataFn(item);
    }
    const currentValue = JSON.parse(JSON.stringify(formStateHandlers.getData() || {}));
    if (extractedData) {
      extractedData.forEach(({ fieldName, value }) => {
        setNestedValue(currentValue, fieldName, value);
      });
    }
    formStateHandlers.setData(currentValue);
    setOnInput(true);
    setCurrentSelection(item);
    setShowResults(false);
    setShowPanel(false);
    setTimeout(() => {
      try {
        if (inputElRef.current) {
          inputElRef.current.focus();
          if (typeof inputElRef.current.select === "function") inputElRef.current.select();
        }
      } catch (err) {
        // ignore
      }
    }, 0);
  }

  // Classes CSS modernes pour l'autocomplete
  const getAutoCompleteInputClasses = (
    formStateHandlers: U2000FormStateHandlers<any>,
    fieldName: string,
    isFocused: boolean,
    readonly?: boolean,
  ) => {
    const sizeClasses = compactGrid ? "h-8 min-h-8 max-h-8 py-0.5" : "py-3";
    const padX = compactGrid ? "px-2" : "px-4";
    // BL-0349 : un seul fond par état (pas bg-surface + bg-muted) — aligné Input / overlay sélection
    const baseClasses = `w-full ${padX} ${sizeClasses} text-sm border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2`;
    const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
    const fieldNameId = fieldName + ".id";
    const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldNameId]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldNameId]);

    if (isDisabled) {
      return `${baseClasses} bg-muted text-gray-500 surface-border cursor-not-allowed`;
    }

    if (hasError) {
      return `${baseClasses} bg-surface border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
    }

    if (isFocused) {
      return `${baseClasses} bg-surface border-blue-500 ring-blue-500/20 text-gray-900 placeholder:text-gray-400`;
    }

    return `${baseClasses} bg-surface border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 text-gray-900 placeholder:text-gray-400 hover:border-gray-400`;
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | globalThis.MouseEvent) {
      const target = event.target as Node;
      if (!target) return;
      const insideContainer = containerRef.current?.contains(target) === true;
      const insideDropdown = dropdownContainerRef.current?.contains(target) === true;
      const outside = !insideContainer && !insideDropdown;
      if (outside) {
        setOnInput(false);
        setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [containerRef, dropdownContainerRef]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current != null) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, []);

  const closeOverlaysOnFocusLeave = useCallback(() => {
    setShowResults(false);
    setOnInput(false);
    setShowPanel(false);
  }, []);

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Tab") {
      return;
    }
    closeOverlaysOnFocusLeave();
  };

  const handleContainerFocusOut = (e: FocusEvent<HTMLDivElement>) => {
    const related = e.relatedTarget as Node | null;
    const shouldClose = u2000ShouldCloseAutoCompleteOnRelatedFocus({
      relatedTarget: related,
      isInsideContainer: related != null && containerRef.current?.contains(related) === true,
      isInsideDropdown: related != null && dropdownContainerRef.current?.contains(related) === true,
      isInsideF8Panel: related != null && f8PanelRef.current?.contains(related) === true,
    });
    if (!shouldClose) {
      return;
    }
    closeOverlaysOnFocusLeave();
  };

  return (
    <div
      className="relative w-full space-y-1.5"
      ref={containerRef}
      style={{ minWidth: 0 }}
      onBlur={handleContainerFocusOut}
    >
      {/* Input — même espacement label / champ que U2000InputText (space-y-1.5) */}
      <div className="relative w-full space-y-1.5">
        {label && <U2000Label label={label} required={required} htmlFor={fieldName} />}
        <div className="relative w-full">
          <input
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showResults && !isDisabled}
            aria-controls={resultListIdRef.current}
            aria-activedescendant={activeOptionId}
            className={`${getAutoCompleteInputClasses(formStateHandlers, fieldName, onInput, readonly)}${
              renderPanel && !isDisabled ? (compactGrid ? " pr-9" : " pr-14") : ""
            }`}
            data-u2000-skip-grid-compact=""
            id={fieldName}
            ref={inputElRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setCurrentSelection(null);
            }}
            onFocus={(e) => {
              hasUserFocusedRef.current = true;
              if (blurTimeoutRef.current != null) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
              }
              setOnInput(true);
              // Ne pas vider la liste si le focus revient depuis le dropdown / le conteneur
              // (faux blur + refocus au montage de la popup — régression vue avec Tailwind 4).
              const related = e.relatedTarget as Node | null;
              const fromInside =
                (related != null && containerRef.current?.contains(related) === true) ||
                (related != null && dropdownContainerRef.current?.contains(related) === true);
              if (!fromInside) {
                if (!currentSelection) lastSearchedValue.current = "";
                setResults([]);
                setSearchMeta({});
                setShowResults(false);
              }
              // Sélectionne tout le texte de l'input
              if (e && e.target && typeof e.target.select === "function") {
                setTimeout(() => e.target.select(), 0);
              }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              if (blurTimeoutRef.current != null) {
                clearTimeout(blurTimeoutRef.current);
              }
              const related = e.relatedTarget as Node | null;
              if (
                (related != null && containerRef.current?.contains(related) === true) ||
                (related != null && dropdownContainerRef.current?.contains(related) === true)
              ) {
                return;
              }
              blurTimeoutRef.current = window.setTimeout(() => {
                blurTimeoutRef.current = null;
                // Ignorer les faux blur (focus toujours sur l'input ou dans le dropdown)
                const active = document.activeElement;
                if (active === inputElRef.current) return;
                if (active != null && containerRef.current?.contains(active)) return;
                if (active != null && dropdownContainerRef.current?.contains(active)) return;
                setOnInput(false);
                setShowResults(false);
                formStateHandlers.onBlur?.(e);
              }, 150);
            }}
            onKeyDown={handleInputKeyDown}
            disabled={readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
            readOnly={readonly}
            placeholder={placeholder}
          />
          {currentSelection && !onInput && (
            <div
              className={`absolute top-0 left-0 z-20 flex ${compactGrid ? "h-8 min-h-8 max-h-8 px-2" : "h-[46px] px-4"} w-full items-center rounded-lg text-sm transition-all duration-200 ${
                isDisabled
                  ? canOpenEntityLink
                    ? "bg-muted border surface-border text-gray-500"
                    : "bg-muted border surface-border text-gray-500 cursor-not-allowed"
                  : "bg-surface border border-gray-300 cursor-pointer"
              }`}
              tabIndex={-1}
              onClick={
                !isDisabled
                  ? () => {
                      setTimeout(() => {
                        const input = containerRef.current ? containerRef.current.querySelector("input") : null;
                        if (input) input.focus();
                      }, 0);
                    }
                  : undefined
              }
              style={{
                pointerEvents: isDisabled && !canOpenEntityLink ? "none" : "auto",
              }}
            >
              {canOpenEntityLink && entityFormPath ? (
                <a
                  href={u2000BuildAppRouteHref(entityFormPath)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`underline truncate max-w-full ${isDisabled ? "text-blue-700 hover:text-blue-900" : "text-blue-700 hover:text-blue-900"}`}
                  title={openEntityInNewTabLabel}
                  aria-label={openEntityInNewTabLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {selectedDisplayText}
                </a>
              ) : (
                <span className={`underline ${isDisabled ? "text-gray-500" : "text-gray-900"}`}>
                  {selectedDisplayText}
                </span>
              )}
            </div>
          )}
          {renderPanel && !isDisabled ? (
            <button
              type="button"
              tabIndex={-1}
              data-u2000-skip-grid-compact=""
              className={`absolute top-1/2 z-30 flex -translate-y-1/2 items-center text-gray-400 transition-colors duration-200 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 rounded-sm ${
                compactGrid ? "right-0.5 flex-row gap-0" : "right-2 flex-col gap-0.5"
              }`}
              onClick={handleOpenPanel}
              title={t("keyboardHintAdvancedSearch")}
              aria-label={t("keyboardHintAdvancedSearch")}
            >
              {loading ? <Spinner /> : <FaSearch className={compactGrid ? "h-3 w-3 text-gray-500" : "h-4 w-4 text-gray-500"} />}
              <U2000KeyboardKeyHint className={compactGrid ? "scale-[0.65] leading-none" : "scale-90"}>F8</U2000KeyboardKeyHint>
            </button>
          ) : (
            <span
              className={`absolute top-1/2 z-30 -translate-y-1/2 cursor-not-allowed text-gray-300 ${compactGrid ? "right-1" : "right-3"}`}
            >
              {loading ? <Spinner /> : <FaSearch className={compactGrid ? "h-3 w-3 text-gray-400" : "h-4 w-4 text-gray-400"} />}
            </span>
          )}
        </div>
      </div>
      {/* Panel personnalisé en overlay, positionné à gauche du composant par défaut */}
      {renderPanel && showPanel && !isDisabled && (
        <div
          ref={f8PanelRef}
          data-u2000-autocomplete-panel="true"
          className="fixed z-50 mt-0 resize overflow-auto rounded-lg border surface-border bg-surface p-3 shadow-xl animate-fade-in"
          onMouseDown={(e) => {
            // Ne pas bloquer le focus natif sur les critères éditables (BL-0347)
            // ni le drag de la poignée CSS resize (BL-0350).
            if (
              !u2000IsEditableMouseDownTarget(e.target) &&
              !u2000IsCssResizeHandleMouseDown(f8PanelRef.current, e.clientX, e.clientY)
            ) {
              e.preventDefault();
            }
            e.stopPropagation();
          }}
          style={{
            left: inputPos.x,
            top: inputPos.y - 6,
            minWidth: 300,
            maxWidth: 900,
            width:
              userPanelSize.width ??
              (panelWidth !== undefined ? panelWidth : containerRef.current?.offsetWidth),
            minHeight: 100,
            maxHeight: 700,
            height: userPanelSize.height ?? panelHeight,
            overflowY: userPanelSize.height != null || panelHeight ? "auto" : undefined,
          }}
        >
          {typeof renderPanel === "function"
            ? renderPanel({
                value,
                setValue,
                setResults,
                setShowResults,
                panelFields,
                setPanelFields,
                onPanelSearch: handlePanelSearch,
                results,
                totalCount: searchMeta.totalCount,
                truncated: searchMeta.truncated,
                displayFn,
                displayField,
                setShowPanel,
                handleRowClickFn: handleRowClick,
              })
            : renderPanel}
        </div>
      )}

      {/* Affichage des résultats simple uniquement si le panel n'est pas affiché */}
      {showResults && results.length > 0 && !showPanel && onInput && !isDisabled && (
        <div
          ref={dropdownContainerRef}
          onMouseDown={(e) => {
            // Empêche le blur de l'input (sinon la liste se referme avant le clic).
            e.preventDefault();
            e.stopPropagation();
          }}
          // Ne pas ajouter d'overflow/maxHeight ici : laisser le scroll au tableau interne
          className="fixed z-40 mt-0 rounded-lg border surface-border bg-surface shadow-xl animate-slide-up"
          style={{
            left: inputPos.x,
            // Soustraire la hauteur du feedback d'erreur s'il est visible pour ne pas décaler
            // le positionnement du dropdown quand le message d'erreur est affiché.
            top: inputPos.y + (inputElRef.current?.offsetHeight || 40) - 2,
            width: dropdownWidth ?? containerRef.current?.offsetWidth ?? 300,
          }}
        >
          <U2000AutoCompleteInputResultTable
            results={results}
            resultLabels={rest.resultLabels}
            resultFields={rest.resultFields}
            handleRowClick={handleRowClick}
            showResults={showResults}
            setShowResults={setShowResults}
            resultTableHeight={rest.basicResultHeight || 200}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            listId={resultListIdRef.current}
            showHeader={rest.resultTableShowHeader !== false}
            formatResultCell={rest.formatResultCell}
            resolveShowResultHeader={rest.resolveShowResultHeader}
            totalCount={searchMeta.totalCount}
            truncated={searchMeta.truncated}
          />
        </div>
      )}

      <div ref={invalidFeedbackRef}>
        <U2000InvalidFeedbackAutoComplete formStateHandlers={formStateHandlers} fieldName={fieldName} />
      </div>
    </div>
  );
};

// Utilitaire pour convertir un objet {field1: value1, field2: value2} en tableau [{fieldName, value}]
function criteriasToFieldArray(criterias: GenericObject): { fieldName: string; value: any }[] {
  return Object.entries(criterias)
    .filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    })
    .map(([fieldName, value]) => ({ fieldName, value }));
}

export default U2000AutoCompleteInput;
